import {
  CreateDateColumn,
  UpdateDateColumn,
  DeleteDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn,
  EntityManager
} from 'typeorm'
import { ObjectType, Field, ID } from 'type-graphql'

import { Domain } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'

@Entity()
@Index('ix_kpi_org_scope_0', (orgScope: KpiOrgScope) => [orgScope.domain, orgScope.entityType, orgScope.entityId], {
  unique: true
})
@ObjectType({
  description:
    'KPI organization scope mapping entity. Maps business entities to various scope dimensions for KPI analysis using generic reference pattern.'
})
export class KpiOrgScope {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for this org-scope mapping.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain (tenant) to which this org-scope mapping belongs.' })
  domain?: Domain

  @RelationId((orgScope: KpiOrgScope) => orgScope.domain)
  @Field({ nullable: true, description: 'ID of the domain (tenant) for this org-scope mapping.' })
  domainId?: string

  @Column()
  @Field({ description: 'Type of the referenced business entity (e.g., Project, Department, Company, Employee)' })
  entityType: string

  @Column()
  @Field({ description: 'ID of the referenced business entity' })
  entityId: string

  @Column()
  @Field({ description: 'Name of the referenced business entity (denormalized for performance and convenience)' })
  entityName: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Legacy org field for backward compatibility' })
  org?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'First scope dimension (e.g., geographical region, business unit)' })
  scope01?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Second scope dimension (e.g., product line, customer segment)' })
  scope02?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Third scope dimension (e.g., process type, technology stack)' })
  scope03?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Fourth scope dimension (e.g., cost center, profit center)' })
  scope04?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Fifth scope dimension (e.g., risk level, priority tier)' })
  scope05?: string

  @CreateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this org-scope mapping was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this org-scope mapping was last updated.' })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this org-scope mapping.' })
  creator?: User

  @RelationId((orgScope: KpiOrgScope) => orgScope.creator)
  @Field({ nullable: true, description: 'ID of the user who created this org-scope mapping.' })
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this org-scope mapping.' })
  updater?: User

  @RelationId((orgScope: KpiOrgScope) => orgScope.updater)
  @Field({ nullable: true, description: 'ID of the user who last updated this org-scope mapping.' })
  updaterId?: string

  // Helper methods for Generic Reference Pattern
  async getReferencedEntity(entityManager: EntityManager): Promise<any> {
    try {
      const repository = entityManager.getRepository(this.entityType)
      return await repository.findOne({ where: { id: this.entityId } })
    } catch (error) {
      console.warn(`Failed to get referenced entity ${this.entityType}:${this.entityId}`, error)
      return null
    }
  }

  async validateEntityReference(entityManager: EntityManager): Promise<boolean> {
    try {
      const entity = await this.getReferencedEntity(entityManager)
      return !!entity
    } catch {
      return false
    }
  }

  // Convenience getter for display purposes
  get displayName(): string {
    return this.entityName || `${this.entityType}:${this.entityId}` || this.org || 'Unknown'
  }
}
